import { forwardRef, type ImgHTMLAttributes } from "react";
import { AvatarImage } from "@/components/ui/avatar";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useNativeResolvableUrl } from "@/lib/native/useNativeResolvableUrl";
import { cn } from "@/lib/utils";

type NativeImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  /** Gray pulse while presigned URL resolves on native. Use "none" on dark galleries. */
  placeholder?: "pulse" | "none";
};

/**
 * img that resolves presigned S3 URLs via blob fetch on Capacitor native.
 * On web, passes src through unchanged.
 */
export const NativeImage = forwardRef<HTMLImageElement, NativeImageProps>(
  function NativeImage(
    { src, className, style, alt, placeholder = "pulse", ...props },
    ref,
  ) {
    const { isNative } = useNativePlatform();
    const nativeSrc =
      isNative && typeof src === "string" ? src : null;
    const resolved = useNativeResolvableUrl(nativeSrc);

    if (!nativeSrc) {
      return (
        <img ref={ref} src={src} className={className} style={style} alt={alt} {...props} />
      );
    }

    if (!resolved) {
      if (placeholder === "none") {
        return null;
      }
      return (
        <div
          className={cn(
            "min-h-[120px] min-w-[120px] bg-muted/80 animate-pulse",
            className,
          )}
          style={style}
          role="img"
          aria-label={alt}
        />
      );
    }

    return (
      <img
        ref={ref}
        src={resolved}
        className={className}
        style={style}
        alt={alt}
        {...props}
      />
    );
  },
);

type NativeAvatarImageProps = React.ComponentProps<typeof AvatarImage>;

export const NativeAvatarImage = forwardRef<
  React.ElementRef<typeof AvatarImage>,
  NativeAvatarImageProps
>(function NativeAvatarImage({ src, className, ...props }, ref) {
  const { isNative } = useNativePlatform();
  const nativeSrc =
    isNative && typeof src === "string" ? src : null;
  const resolved = useNativeResolvableUrl(nativeSrc);

  if (!nativeSrc) {
    return <AvatarImage ref={ref} src={src} className={className} {...props} />;
  }

  if (!resolved) {
    return (
      <div
        className={cn("h-full w-full bg-muted/80 animate-pulse", className)}
        aria-hidden
      />
    );
  }

  return (
    <AvatarImage ref={ref} src={resolved} className={className} {...props} />
  );
});
